import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
  CircularProgress,
  // Stack,
  Divider,
  Badge,
  MenuItem,
  Autocomplete,
  IconButton,
  Card,
  CardContent,
} from '@mui/material';
import { GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { ReusableDataGrid, CustomDrawer, ActionsMenu, type ActionMenuItem } from '@/components/common';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik, FieldArray, FormikProvider } from 'formik';
import * as Yup from 'yup';
import { RootState, AppDispatch } from '@/store/types';
import { useAuth } from '@/hooks';
import {
  fetchMaterials,
  fetchMaterialsList,
  // createMaterial,
  createBulkMaterials,
  updateMaterial,
  deleteMaterial,
  fetchCompanies,
  fetchSuppliers,
} from '@/store';
import { MaterialFormData, BulkMaterialFormData } from '@/services/material/materialService';
import moment from 'moment';

// Validation schema for single material update
const validationSchema = Yup.object({
  materialName: Yup.string().required('Material name is required').min(2, 'Name must be at least 2 characters'),
  companyId: Yup.string().required('Company name is required').min(2, 'Company name must be at least 2 characters'),
  materialType: Yup.string().required('Material type is required'),
  quantityCount: Yup.number().required('Quantity count is required').min(0, 'Quantity count must be non-negative'),
  quantityKg: Yup.number().required('Quantity (kg) is required').min(0, 'Quantity (kg) must be non-negative'),
  description: Yup.string().required('Description is required'),
  arrivedDateTime: Yup.string().required('Arrived date time is required'),
  vehicleNumber: Yup.string().required('Vehicle number is required'),
  supplierId: Yup.string().required('Supplier is required'),
  isActive: Yup.boolean().required('Status is required'),
  remarks: Yup.string(),
  jobType: Yup.string().required('Job type is required'),
});

// Validation schema for bulk material creation
const bulkValidationSchema = Yup.object({
  companyId: Yup.string().required('Company is required'),
  arrivedDateTime: Yup.string().required('Arrived date time is required'),
  vehicleNumber: Yup.string().required('Vehicle number is required'),
  supplierId: Yup.string().required('Supplier is required'),
  documentNumber: Yup.string().required('Document No is required'),
  jobType: Yup.string().required('Job type is required'),
  remarks: Yup.string().optional(),
  materials: Yup.array()
    .of(
      Yup.object().shape({
        materialId: Yup.string().optional(),
        materialName: Yup.string().when('materialId', {
          is: (val: string) => !val,
          then: (schema) => schema.required('Material name is required'),
          otherwise: (schema) => schema.optional(),
        }),
        materialType: Yup.string().when('materialId', {
          is: (val: string) => !val,
          then: (schema) => schema.required('Material type is required'),
          otherwise: (schema) => schema.optional(),
        }),
        quantityCount: Yup.number().required('Quantity count is required').min(0, 'Must be non-negative'),
        quantityKg: Yup.number().required('Quantity (kg) is required').min(0, 'Must be non-negative'),
      })
    )
    .min(1, 'At least one material is required'),
});

// const materialTypes = [
//   'Construction Material',
//   'Raw Material',
//   'Electronic Components',
//   'Chemical',
//   'Metal',
//   'Plastic',
//   'Textile',
//   'Food Grade',
//   'Medical',
//   'Industrial',
//   'Other',
// ];

const initialFormData: MaterialFormData = {
  materialName: '',
  companyId: '',
  materialType: '',
  quantityCount: 0,
  quantityKg: 0,
  description: '',
  arrivedDateTime: moment().format('YYYY-MM-DDTHH:mm'),
  vehicleNumber: '',
  supplierId: '',
  isActive: true,
  remarks: '',
  jobType: '',
};

// Initial form data for bulk material creation
const initialBulkFormData = {
  companyId: '',
  arrivedDateTime: moment().format('YYYY-MM-DDTHH:mm'),
  vehicleNumber: '',
  supplierId: '',
  documentNumber: '',
  jobType: 'purchase',
  remarks: '',
  materials: [
    {
      materialId: '',
      materialName: '',
      materialType: '',
      quantityCount: 0,
      quantityKg: 0,
    },
  ],
};

export const ManageMaterial: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useAuth();
  const { materials, materialsList, materialsListLoading, materialsLoading, isLoading, pagination } = useSelector(
    (state: RootState) => state.material
  );
  const { companies } = useSelector((state: RootState) => state.company);
  const { suppliers } = useSelector((state: RootState) => state.supplier);

  const [openDrawer, setOpenDrawer] = useState(false);
  const [openBulkDrawer, setOpenBulkDrawer] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isActiveFilter, setIsActiveFilter] = useState<boolean | ''>('');
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });

  // Formik for single material edit (only for updating existing materials)
  const formik = useFormik({
    initialValues: initialFormData,
    validationSchema,
    onSubmit: async (values) => {
      try {
        if (!editingId) {
          console.error('No material ID found for update');
          return;
        }

        const submitData = {
          ...values,
          companyId: values.companyId || undefined,
          supplierId: values.supplierId || undefined,
          userId: user?._id || user?.id
        };

        await dispatch(updateMaterial({
          id: editingId,
          data: submitData
        }));
        handleCloseDrawer();
        loadData();
      } catch (error) {
        console.error('Error updating material:', error);
      }
    },
  });

  // Formik for bulk material creation
  const bulkFormik = useFormik({
    initialValues: initialBulkFormData,
    validationSchema: bulkValidationSchema,
    onSubmit: async (values) => {
      try {
        // Clean up materials array - remove materialName and materialType when materialId is present
        const cleanedMaterials = values.materials.map((material) => {
          if (material.materialId) {
            // For existing materials, only send materialId and quantities
            return {
              materialId: material.materialId,
              quantityCount: material.quantityCount,
              quantityKg: material.quantityKg,
            };
          } else {
            // For new materials, send name, type, and quantities (no materialId)
            return {
              materialName: material.materialName,
              materialType: material.materialType,
              quantityCount: material.quantityCount,
              quantityKg: material.quantityKg,
            };
          }
        });

        const submitData: BulkMaterialFormData & { userId?: string } = {
          companyId: values.companyId,
          arrivedDateTime: values.arrivedDateTime,
          vehicleNumber: values.vehicleNumber,
          supplierId: values.supplierId,
          documentNumber: values.documentNumber,
          jobType: values.jobType,
          remarks: values.remarks,
          materials: cleanedMaterials,
          userId: user?._id || user?.id,
        };

        await dispatch(createBulkMaterials(submitData));
        // handleCloseBulkDrawer();
        loadData();
      } catch (error) {
        console.error('Error saving materials:', error);
      }
    },
  });

  const loadData = useCallback(() => {
    dispatch(fetchMaterials({ 
      page: paginationModel.page + 1,
      limit: paginationModel.pageSize,
      search: searchTerm || undefined,
      isActive: isActiveFilter !== '' ? isActiveFilter : undefined
    }));
  }, [dispatch, paginationModel, searchTerm, isActiveFilter]);

  const loadRelatedData = useCallback(() => {
    dispatch(fetchCompanies({ limit: 100 }));
    dispatch(fetchSuppliers({ limit: 100 }));
  }, [dispatch]);

  useEffect(() => {
    loadData();
    loadRelatedData();
  }, [loadData, loadRelatedData]);

  // Fetch filtered materials list when company and supplier are selected in bulk form
  useEffect(() => {
    const companyId = bulkFormik.values.companyId;
    const supplierId = bulkFormik.values.supplierId;
    const jobType = bulkFormik.values.jobType;

    if (companyId && supplierId) {
      dispatch(fetchMaterialsList({
        page: 1,
        limit: 1000,
        companyId,
        supplierId,
        jobType
      }));
    }
  }, [bulkFormik.values.companyId, bulkFormik.values.supplierId, bulkFormik.values.jobType, dispatch]);

  const handleOpenDrawer = (material: any) => {
    // Only open for editing existing materials
    if (!material) {
      console.error('Material data is required for editing');
      return;
    }

    formik.setValues({
      materialName: material.materialName,
      companyId: material.companyId?._id || '',
      materialType: material.materialType,
      quantityCount: material.quantityCount,
      quantityKg: material.quantityKg,
      description: material.description,
      arrivedDateTime: material.arrivedDateTime ? moment(material.arrivedDateTime).format('YYYY-MM-DDTHH:mm') : '',
      vehicleNumber: material.vehicleNumber,
      supplierId: material.supplierId?._id || '',
      isActive: material.isActive,
      remarks: material.remarks || '',
      jobType: material.jobType || '',
    });
    setEditingId(material._id);
    setOpenDrawer(true);
  };

  const handleCloseDrawer = () => {
    setOpenDrawer(false);
    formik.resetForm();
    setEditingId(null);
  };

  const handleOpenBulkDrawer = () => {
    bulkFormik.resetForm();
    setOpenBulkDrawer(true);
  };

  const handleCloseBulkDrawer = () => {
    setOpenBulkDrawer(false);
    bulkFormik.resetForm();
  };

  const handleDelete = async (id: string) => {
    try {
      await dispatch(deleteMaterial(id));
      setDeleteConfirmId(null);
      loadData();
    } catch (error) {
      console.error('Error deleting material:', error);
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setIsActiveFilter('');
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (searchTerm) count++;
    if (isActiveFilter !== '') count++;
    return count;
  };

  const activeFilterCount = getActiveFilterCount();

  const getStatusColor = (isActive: boolean) => {
    return isActive ? 'success' : 'default';
  };

  // const getCompanyName = (companyId: string | null) => {
  //   if (!companyId) return 'N/A';
  //   const company = companies.find(c => c._id === companyId);
  //   return company ? company.name : 'N/A';
  // };

  // const getCompanyName = (companyId: string | null|any) => {
  //   console.log('companyId', companyId);
  //   if (!companyId?.value?._id) return 'N/A';
  //   const company = companies.find(c => c._id === companyId?.value?._id);
  //   return company ? company.name : 'N/A';
  // }

  const getSupplierName = (supplierId: string | null|any) => {
    if (!supplierId?.value?._id) return 'N/A';
    const supplier = suppliers.find(s => s._id === supplierId?.value?._id);
    return supplier ? supplier.companyName : 'N/A';
  };

  const formatDateTime = (dateTimeString: string) => {
    if (!dateTimeString) return 'N/A';
    const date = moment(dateTimeString);
    return date.isValid() ? date.format('DD/MM/YYYY HH:mm') : 'Invalid Date';
  };

  const columns: GridColDef[] = [
    {
      field: 'materialName',
      headerName: 'Material Name',
      flex: 1,
      minWidth: 180,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="medium">
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'materialType',
      headerName: 'Type',
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color="primary"
          size="small"
          variant="outlined"
        />
      ),
    },{
      field: 'initialCount',
      headerName: 'Initial Count',
      flex: 0.6,
      minWidth: 80,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="bold">
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'quantityCount',
      headerName: 'Current Count',
      flex: 0.6,
      minWidth: 80,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="bold">
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'initialKg',
      headerName: 'Initial Weight',
      flex: 0.8,
      minWidth: 100,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="bold">
          {params.value} kg
        </Typography>
      ),
    },
    {
      field: 'quantityKg',
      headerName: 'Current Weight',
      flex: 0.8,
      minWidth: 100,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="bold">
          {params.value} kg
        </Typography>
      ),
    },
    {
      field: 'supplierId',
      headerName: 'Supplier',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Typography variant="body2" color="primary">
          {getSupplierName(params)}
        </Typography>
      ),
    },
    {
      field: 'vehicleNumber',
      headerName: 'Vehicle',
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'arrivedDateTime',
      headerName: 'Arrived',
      flex: 1,
      minWidth: 140,
      renderCell: (params) => (
        <Typography variant="body2">
          {formatDateTime(params.value)}
        </Typography>
      ),
    },
      {
      field: 'jobType',
      headerName: 'Job Type',
      flex: 0.6,
      minWidth: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? params.value : 'N/A'}
          color={getStatusColor(params.value) as any}
          size="small"
        />
      ),
    },
    {
      field: 'isActive',
      headerName: 'Status',
      flex: 0.6,
      minWidth: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Active' : 'Inactive'}
          color={getStatusColor(params.value) as any}
          size="small"
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const actions: ActionMenuItem[] = [
          {
            id: 'edit',
            title: 'Edit',
            onClick: () => handleOpenDrawer(params.row),
            disabled: isLoading,
          },
          {
            id: 'delete',
            title: 'Delete',
            onClick: () => setDeleteConfirmId(params.row._id),
            disabled: isLoading,
          },
        ];

        return (
          <ActionsMenu
            actions={actions}
            disabled={isLoading}
          />
        );
      },
    },
  ];

  const supplierOptions = suppliers.map(supplier => ({
    id: supplier._id,
    label: supplier.companyName,
    value: supplier._id,
  }));

  const selectedSupplier = supplierOptions.find(option => option.value === formik.values.supplierId) || null;

  return (
    <Box>
      {/* Header */}
      <Box mb={3}>
        <Box display="flex" alignItems="center" mb={2}>
          <Typography variant="h5" component="h5" flexGrow={1}>
            Manage Materials
          </Typography>
        </Box>
        <Divider />
      </Box>

      {/* Filter and Action Buttons */}
      <Grid container spacing={2} alignItems="center" justifyContent="flex-end" sx={{ mb: 3 }}>
        <Grid size="auto">
          <Badge badgeContent={activeFilterCount} color="primary">
            <Button
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={() => setFilterDrawerOpen(true)}
            >
              Filters
            </Button>
          </Badge>
        </Grid>
        <Grid size="auto">
          <Button
            variant="contained"
            onClick={handleOpenBulkDrawer}
            disabled={isLoading}
          >
            Add Materials
          </Button>
        </Grid>
      </Grid>

      <ReusableDataGrid
        rows={materials || []}
        columns={columns}
        getRowId={(row) => row?._id}
        loading={materialsLoading}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        paginationData={pagination ? { totalRecords: pagination.totalItems } : undefined}
        noRowsMessage="No materials found"
        noRowsActionLabel="Add Materials"
        onNoRowsAction={handleOpenBulkDrawer}
      />

      {/* Edit Drawer */}
      <CustomDrawer
        open={openDrawer}
        onClose={handleCloseDrawer}
        title="Edit Material"
        subtitle="Update material details"
        width={'100%'}
        disableBackdropClick={true}
      >
        {/* <form onSubmit={formik.handleSubmit}> */}
          <Box sx={{ py: 3 }}>
            <Grid container spacing={3}>
              <Grid size={{xs: 12, sm: 6}}>
                <TextField
                  fullWidth
                  label="Material Name"
                  required

                  name="materialName"
                  value={formik.values.materialName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.materialName && Boolean(formik.errors.materialName)}
                  helperText={formik.touched.materialName && formik.errors.materialName}
                  placeholder="e.g., Steel Rods"
                />
              </Grid>
              <Grid size={{xs: 12, sm: 6}}>
                <TextField
                  fullWidth
                  // select
                  required
                  label="Material Type"
                  name="materialType"
                  value={formik.values.materialType}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.materialType && Boolean(formik.errors.materialType)}
                  helperText={formik.touched.materialType && formik.errors.materialType}
                  variant="outlined"
                >
                  {/* {materialTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))} */}
                </TextField>
              </Grid>
              <Grid size={{xs: 12}}>
                 <TextField
                  fullWidth
                  select
                  label="Job Type"
                  name="jobType"
                  required
                  value={formik.values.jobType}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.jobType && Boolean(formik.errors.jobType)}
                  helperText={formik.touched.jobType && formik.errors.jobType}
                  variant="outlined"
                >
                  <MenuItem value="purchase">Purchase</MenuItem>
                  <MenuItem value="jobwork">Jobwork</MenuItem>
                  {/* <MenuItem value="transfer">Transfer</MenuItem> */}
                </TextField>
                </Grid>
              <Grid size={{xs: 12}}>
                <TextField
                  fullWidth
                  select
                  label="Link to Company"
                  name="companyId"
                  required
                  value={formik.values.companyId}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.companyId && Boolean(formik.errors.companyId)}
                  helperText={formik.touched.companyId && formik.errors.companyId}
                  variant="outlined"
                >
                  <MenuItem value="">No Company Link</MenuItem>
                  {companies.map((company) => (
                    <MenuItem key={company._id} value={company._id}>
                      {company.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{xs: 12, sm: 6}}>
                <TextField
                  fullWidth
                  type="number"
                  required

                  label="Quantity Count"
                  name="quantityCount"
                  value={formik.values.quantityCount}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.quantityCount && Boolean(formik.errors.quantityCount)}
                  helperText={formik.touched.quantityCount && formik.errors.quantityCount}
                  placeholder="e.g., 100"
                />
              </Grid>
              <Grid size={{xs: 12, sm: 6}}>
                <TextField
                  fullWidth
                  type="number"
                  label="Quantity (kg)"
                  required

                  name="quantityKg"
                  value={formik.values.quantityKg}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.quantityKg && Boolean(formik.errors.quantityKg)}
                  helperText={formik.touched.quantityKg && formik.errors.quantityKg}
                  placeholder="e.g., 500.5"
                  inputProps={{ step: "0.1" }}
                />
              </Grid>
              <Grid size={{xs: 12}}>
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  required
                  multiline
                  rows={3}
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.description && Boolean(formik.errors.description)}
                  helperText={formik.touched.description && formik.errors.description}
                  placeholder="e.g., High quality steel rods for construction"
                />
              </Grid>
              <Grid size={{xs: 12, sm: 6}}>
                <TextField
                  fullWidth
                  type="datetime-local"
                  label="Arrived Date & Time"
                  name="arrivedDateTime"
                  required

                  value={formik.values.arrivedDateTime}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.arrivedDateTime && Boolean(formik.errors.arrivedDateTime)}
                  helperText={formik.touched.arrivedDateTime && formik.errors.arrivedDateTime}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid size={{xs: 12, sm: 6}}>
                <TextField
                  fullWidth
                  label="Vehicle Number"
                  name="vehicleNumber"
                  required

                  value={formik.values.vehicleNumber}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.vehicleNumber && Boolean(formik.errors.vehicleNumber)}
                  helperText={formik.touched.vehicleNumber && formik.errors.vehicleNumber}
                  placeholder="e.g., MH12AB1234"
                />
              </Grid>
              <Grid size={{xs: 12}}>
                <Autocomplete
                  options={supplierOptions}
                  value={selectedSupplier}
                  onChange={(_, newValue) => {
                    formik.setFieldValue('supplierId', newValue?.value || '');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Supplier "
                      name="supplierId"
                  required

                      onBlur={formik.handleBlur}
                      error={formik.touched.supplierId && Boolean(formik.errors.supplierId)}
                      helperText={formik.touched.supplierId && formik.errors.supplierId}
                      placeholder="Search and select supplier..."
                    />
                  )}
                  isOptionEqualToValue={(option, value) => option.value === value?.value}
                  noOptionsText="No suppliers found"
                />
              </Grid>
              <Grid size={{xs: 12}}>
                <TextField
                  fullWidth
                  select
                  name="isActive"
                  label="Status"
                  value={formik.values.isActive}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.isActive && Boolean(formik.errors.isActive)}
                  helperText={formik.touched.isActive && formik.errors.isActive}
                  variant="outlined"
                  slotProps={{
                    select: {
                      native: true,
                    },
                  }}
                >
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </TextField>
              </Grid>
              <Grid size={{xs: 12}}>
                <TextField
                  fullWidth
                  label="Remarks (Optional)"
                  name="remarks"
                  multiline
                  rows={3}
                  value={formik.values.remarks}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.remarks && Boolean(formik.errors.remarks)}
                  helperText={formik.touched.remarks && formik.errors.remarks}
                  placeholder="Additional notes about the material..."
                />
              </Grid>
            </Grid>
          </Box>
          <Box sx={{ p: 3, pt: 0, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button onClick={handleCloseDrawer} disabled={isLoading}>
              Cancel
            </Button>
            <Button
              onClick={(e:any) => formik.handleSubmit(e)}
              variant="contained"
              disabled={isLoading}
            >
              {isLoading ? <CircularProgress size={20} /> : 'Update'}
            </Button>
          </Box>
        {/* </form> */}
      </CustomDrawer>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirmId} onClose={() => setDeleteConfirmId(null)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this material? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmId(null)}>Cancel</Button>
          <Button
            onClick={() => deleteConfirmId && handleDelete(deleteConfirmId)}
            color="error"
            variant="contained"
            disabled={isLoading}
          >
            {isLoading ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Create Drawer */}
      <CustomDrawer
        open={openBulkDrawer}
        onClose={handleCloseBulkDrawer}
        title="Add Materials"
        subtitle="Add new materials or update existing ones"
        width={'100%'}
        disableBackdropClick={true}
      >
        <FormikProvider value={bulkFormik}>
          <Box sx={{ py: 3 }}>
            <Grid container spacing={3}>
              {/* Common Fields */}
              <Grid size={{xs: 12}}>
                <Typography variant="h6" gutterBottom>
                  Delivery Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid size={{xs: 12, sm: 6}}>
                <TextField
                  fullWidth
                  select
                  label="Company"
                  name="companyId"
                  required
                  value={bulkFormik.values.companyId}
                  onChange={bulkFormik.handleChange}
                  onBlur={bulkFormik.handleBlur}
                  error={bulkFormik.touched.companyId && Boolean(bulkFormik.errors.companyId)}
                  helperText={bulkFormik.touched.companyId && bulkFormik.errors.companyId}
                  variant="outlined"
                >
                  <MenuItem value="">Select Company</MenuItem>
                  {companies.map((company) => (
                    <MenuItem key={company._id} value={company._id}>
                      {company.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid size={{xs: 12, sm: 6}}>
                <Autocomplete
                  options={supplierOptions}
                  value={supplierOptions.find(option => option.value === bulkFormik.values.supplierId) || null}
                  onChange={(_, newValue) => {
                    bulkFormik.setFieldValue('supplierId', newValue?.value || '');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Supplier"
                      name="supplierId"
                      required
                      onBlur={bulkFormik.handleBlur}
                      error={bulkFormik.touched.supplierId && Boolean(bulkFormik.errors.supplierId)}
                      helperText={bulkFormik.touched.supplierId && bulkFormik.errors.supplierId}
                      placeholder="Search and select supplier..."
                    />
                  )}
                  isOptionEqualToValue={(option, value) => option.value === value?.value}
                  noOptionsText="No suppliers found"
                />
              </Grid>

              <Grid size={{xs: 12, sm: 6}}>
                <TextField
                  fullWidth
                  type="datetime-local"
                  label="Arrived Date & Time"
                  name="arrivedDateTime"
                  required
                  value={bulkFormik.values.arrivedDateTime}
                  onChange={bulkFormik.handleChange}
                  onBlur={bulkFormik.handleBlur}
                  error={bulkFormik.touched.arrivedDateTime && Boolean(bulkFormik.errors.arrivedDateTime)}
                  helperText={bulkFormik.touched.arrivedDateTime && bulkFormik.errors.arrivedDateTime}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>

              <Grid size={{xs: 12, sm: 6}}>
                <TextField
                  fullWidth
                  label="Vehicle Number"
                  name="vehicleNumber"
                  required
                  value={bulkFormik.values.vehicleNumber}
                  onChange={bulkFormik.handleChange}
                  onBlur={bulkFormik.handleBlur}
                  error={bulkFormik.touched.vehicleNumber && Boolean(bulkFormik.errors.vehicleNumber)}
                  helperText={bulkFormik.touched.vehicleNumber && bulkFormik.errors.vehicleNumber}
                  placeholder="e.g., MH12AB1234"
                />
              </Grid>

              <Grid size={{xs: 12, sm: 6}}>
                <TextField
                  fullWidth
                  label="Document Number"
                  required
                  name="documentNumber"
                  value={bulkFormik.values.documentNumber}
                  onChange={bulkFormik.handleChange}
                  onBlur={bulkFormik.handleBlur}
                  error={bulkFormik.touched.documentNumber && Boolean(bulkFormik.errors.documentNumber)}
                  helperText={bulkFormik.touched.documentNumber && bulkFormik.errors.documentNumber}
                  placeholder="e.g., DOC-ABC123"
                />
              </Grid>

              <Grid size={{xs: 12, sm: 6}}>
                <TextField
                  fullWidth
                  select
                  label="Job Type"
                  name="jobType"
                  required
                  value={bulkFormik.values.jobType}
                  onChange={bulkFormik.handleChange}
                  onBlur={bulkFormik.handleBlur}
                  error={bulkFormik.touched.jobType && Boolean(bulkFormik.errors.jobType)}
                  helperText={bulkFormik.touched.jobType && bulkFormik.errors.jobType}
                  variant="outlined"
                >
                  <MenuItem value="purchase">Purchase</MenuItem>
                  <MenuItem value="jobwork">Jobwork</MenuItem>
                  {/* <MenuItem value="transfer">Transfer</MenuItem> */}
                </TextField>
              </Grid>

              <Grid size={{xs: 12}}>
                <TextField
                  fullWidth
                  label="Remarks"
                  name="remarks"
                  multiline
                  rows={2}
                  value={bulkFormik.values.remarks}
                  onChange={bulkFormik.handleChange}
                  onBlur={bulkFormik.handleBlur}
                  error={bulkFormik.touched.remarks && Boolean(bulkFormik.errors.remarks)}
                  helperText={bulkFormik.touched.remarks && bulkFormik.errors.remarks}
                  placeholder="Additional notes..."
                />
              </Grid>

              {/* Materials Array */}
              <Grid size={{xs: 12}}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, mb: 1 }}>
                  <Typography variant="h6">
                    Materials
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid size={{xs: 12}}>
                <FieldArray name="materials">
                  {({ push, remove }) => (
                    <>
                      {bulkFormik.values.materials.map((material, index) => (
                        <Card key={index} sx={{ mb: 2, position: 'relative' }}>
                          <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                              <Typography variant="subtitle1" fontWeight="medium">
                                Material #{index + 1}
                              </Typography>
                              {bulkFormik.values.materials.length > 1 && (
                                <IconButton
                                  color="error"
                                  onClick={() => remove(index)}
                                  size="small"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              )}
                            </Box>

                            <Grid container spacing={2}>
                              {/* Material selection - either existing or new */}
                              <Grid size={{xs: 12}}>
                                <Autocomplete
                                  options={materialsList.map(m => ({
                                    id: m._id,
                                    label: `${m.materialName} (${m.materialType})${m.jobType ? ` - ${m.jobType}` : ''}`,
                                    value: m._id,
                                  }))}
                                  value={materialsList.find(m => m._id === material.materialId) ? {
                                    id: material.materialId,
                                    label: `${materialsList.find(m => m._id === material.materialId)?.materialName} (${materialsList.find(m => m._id === material.materialId)?.materialType})${materialsList.find(m => m._id === material.materialId)?.jobType ? ` - ${materialsList.find(m => m._id === material.materialId)?.jobType}` : ''}`,
                                    value: material.materialId,
                                  } : null}
                                  onChange={(_, newValue) => {
                                    if (newValue) {
                                      bulkFormik.setFieldValue(`materials.${index}.materialId`, newValue.value);
                                      bulkFormik.setFieldValue(`materials.${index}.materialName`, '');
                                      bulkFormik.setFieldValue(`materials.${index}.materialType`, '');
                                    } else {
                                      bulkFormik.setFieldValue(`materials.${index}.materialId`, '');
                                    }
                                  }}
                                  loading={materialsListLoading}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      label="Update Existing Material (Optional)"
                                      placeholder="Search existing materials..."
                                      helperText={
                                        !bulkFormik.values.companyId || !bulkFormik.values.supplierId
                                          ? "Please select company and supplier first to load materials"
                                          : "Select an existing material to update it, or leave empty to create a new one"
                                      }
                                    />
                                  )}
                                  isOptionEqualToValue={(option, value) => option.value === value?.value}
                                  noOptionsText="No existing materials found"
                                  disabled={!bulkFormik.values.companyId || !bulkFormik.values.supplierId}
                                />
                              </Grid>

                              {/* Show name and type fields only if not updating existing material */}
                              {!material.materialId && (
                                <>
                                  <Grid size={{xs: 12, sm: 6}}>
                                    <TextField
                                      fullWidth
                                      label="Material Name"
                                      required
                                      name={`materials.${index}.materialName`}
                                      value={material.materialName}
                                      onChange={bulkFormik.handleChange}
                                      onBlur={bulkFormik.handleBlur}
                                      error={
                                        bulkFormik.touched.materials?.[index]?.materialName &&
                                        Boolean((bulkFormik.errors.materials as any)?.[index]?.materialName)
                                      }
                                      helperText={
                                        bulkFormik.touched.materials?.[index]?.materialName &&
                                        (bulkFormik.errors.materials as any)?.[index]?.materialName
                                      }
                                      placeholder="e.g., Steel Rods"
                                    />
                                  </Grid>

                                  <Grid size={{xs: 12, sm: 6}}>
                                    <TextField
                                      fullWidth
                                      label="Material Type"
                                      required
                                      name={`materials.${index}.materialType`}
                                      value={material.materialType}
                                      onChange={bulkFormik.handleChange}
                                      onBlur={bulkFormik.handleBlur}
                                      error={
                                        bulkFormik.touched.materials?.[index]?.materialType &&
                                        Boolean((bulkFormik.errors.materials as any)?.[index]?.materialType)
                                      }
                                      helperText={
                                        bulkFormik.touched.materials?.[index]?.materialType &&
                                        (bulkFormik.errors.materials as any)?.[index]?.materialType
                                      }
                                      placeholder="e.g., Construction Material"
                                    />
                                  </Grid>
                                </>
                              )}

                              <Grid size={{xs: 12, sm: 6}}>
                                <TextField
                                  fullWidth
                                  type="number"
                                  required
                                  label="Quantity Count"
                                  name={`materials.${index}.quantityCount`}
                                  value={material.quantityCount}
                                  onChange={bulkFormik.handleChange}
                                  onBlur={bulkFormik.handleBlur}
                                  error={
                                    bulkFormik.touched.materials?.[index]?.quantityCount &&
                                    Boolean((bulkFormik.errors.materials as any)?.[index]?.quantityCount)
                                  }
                                  helperText={
                                    bulkFormik.touched.materials?.[index]?.quantityCount &&
                                    (bulkFormik.errors.materials as any)?.[index]?.quantityCount
                                  }
                                  placeholder="e.g., 100"
                                />
                              </Grid>

                              <Grid size={{xs: 12, sm: 6}}>
                                <TextField
                                  fullWidth
                                  type="number"
                                  label="Quantity (kg)"
                                  required
                                  name={`materials.${index}.quantityKg`}
                                  value={material.quantityKg}
                                  onChange={bulkFormik.handleChange}
                                  onBlur={bulkFormik.handleBlur}
                                  error={
                                    bulkFormik.touched.materials?.[index]?.quantityKg &&
                                    Boolean((bulkFormik.errors.materials as any)?.[index]?.quantityKg)
                                  }
                                  helperText={
                                    bulkFormik.touched.materials?.[index]?.quantityKg &&
                                    (bulkFormik.errors.materials as any)?.[index]?.quantityKg
                                  }
                                  placeholder="e.g., 500.5"
                                  inputProps={{ step: "0.1" }}
                                />
                              </Grid>
                            </Grid>
                          </CardContent>
                        </Card>
                      ))}

                      <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => push({
                          materialId: '',
                          materialName: '',
                          materialType: '',
                          quantityCount: 0,
                          quantityKg: 0,
                        })}
                        fullWidth
                      >
                        Add Another Material
                      </Button>
                    </>
                  )}
                </FieldArray>
              </Grid>
            </Grid>
          </Box>
          <Box sx={{ p: 3, pt: 0, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button onClick={handleCloseBulkDrawer} disabled={isLoading}>
              Cancel
            </Button>
            <Button
              onClick={(e:any) => bulkFormik.handleSubmit(e)}
              variant="contained"
              disabled={isLoading}
            >
              {isLoading ? <CircularProgress size={20} /> : 'Create Materials'}
            </Button>
          </Box>
        </FormikProvider>
      </CustomDrawer>

      {/* Filter Drawer */}
      <CustomDrawer
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        title="Filter Materials"
        width={400}
        anchor="right"
        actions={
          <Button
            variant="outlined"
            color="secondary"
            onClick={clearFilters}
            disabled={activeFilterCount === 0}
            startIcon={<FilterIcon />}
            fullWidth
          >
            Clear Filters
          </Button>
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Search materials"
            variant="outlined"
            size="small"
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by name, type, vehicle..."
            slotProps={{
              input: {
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              },
            }}
          />
          
          <TextField
            select
            label="Filter by Status"
            variant="outlined"
            size="small"
            fullWidth
            value={isActiveFilter}
            onChange={(e) => setIsActiveFilter(e.target.value === '' ? '' : e.target.value === 'true')}
            slotProps={{
              select: {
                native: true,
              },
            }}
          >
            <option value="">All Status</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </TextField>
          
          {activeFilterCount > 0 && (
            <Box mt={2}>
              <Typography variant="body2" color="text.secondary">
                {activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''} applied
              </Typography>
            </Box>
          )}
        </Box>
      </CustomDrawer>
    </Box>
  );
};